import type { BlueprintLead } from '@/types/blueprint-leads';
import type { ProofMetric } from '@/types/customer-ops';
import type { Deal, DealStage } from '@/types/deals';
import type { ProspectNurtureSendEvent } from '@/types/prospect-nurture';
import type { OutreachTemplateKey } from '@/types/lead-outreach';
import { CLEARED_PROOF_URLS } from '@/lib/leads/pinch-hit-outreach';
import { buildEmailBody, buildEmailSubject } from '@/lib/outreach/email-copy';
import {
  buildNurturePulseBody,
  buildNurturePulseSubject,
  isNurturePulseBlocked,
  proofUrlForNurturePulse,
} from '@/lib/outreach/nurture-pulse-copy';
import {
  getNurturePulseIssueByTemplate,
  NURTURE_PULSE_TEMPLATE_KEYS,
  suggestNurturePulseIssue,
  type NurturePulseIssue,
} from '@/lib/outreach/nurture-pulse-sequence';
import { resolveNurtureStoreName } from '@/lib/outreach/nurture-store-name';
import { isProofWarmNurtureReady } from '@/lib/utils/proof-warm-nurture-readiness';

export { dealNeedsStoreReview, resolveNurtureStoreName } from '@/lib/outreach/nurture-store-name';
export {
  NURTURE_PULSE_ISSUES,
  NURTURE_PULSE_CADENCE_DAYS,
  suggestNurturePulseIssue,
  formatPulseNurtureNotes,
  isPulseSendDue,
} from '@/lib/outreach/nurture-pulse-sequence';

export interface OutreachContactContext {
  firstName: string;
  storeName: string;
  posVendor: string | null;
  notes: string;
}

export interface NurtureEmailDraft {
  template_key: OutreachTemplateKey;
  subject: string;
  body: string;
  proof_url: string | null;
  mailto_href: string | null;
  pulse_issue?: number | null;
  proof_metric_id?: string | null;
  proof_ready?: boolean;
  /** Set when strict warm-nurture authority blocked the body. No copy is produced. */
  blocked_reasons?: string[] | null;
}

export const NURTURE_TEMPLATES: OutreachTemplateKey[] = [
  ...NURTURE_PULSE_TEMPLATE_KEYS,
  'follow_up',
  'follow_up_2',
];

/** Legacy B1–B4 templates (pain-led). Prefer NURTURE_PULSE_TEMPLATE_KEYS for batch nurture. */
export const LEGACY_NURTURE_TEMPLATES: OutreachTemplateKey[] = [
  'B1_timing',
  'B2_one_station',
  'B3_proposal_followup',
  'B4_peak_day',
];

function firstNameFromFull(name: string | null | undefined): string {
  const person = name?.trim();
  if (!person) return 'there';
  return person.split(/\s+/)[0] || 'there';
}

export function outreachContextFromDeal(deal: Deal): OutreachContactContext {
  const store = resolveNurtureStoreName(deal);
  return {
    firstName: firstNameFromFull(deal.prospect_contact_name),
    storeName: store.storeName,
    posVendor: deal.pos_system,
    notes: [deal.notes, deal.nurture_notes, deal.closed_reason].filter(Boolean).join(' '),
  };
}

function proofUrlForTemplate(templateKey: OutreachTemplateKey, proofMetric?: ProofMetric | null): string | null {
  const pulseIssue = getNurturePulseIssueByTemplate(templateKey);
  if (pulseIssue) {
    return proofUrlForNurturePulse(proofMetric);
  }
  if (templateKey === 'B1_timing' || templateKey === 'B2_one_station' || templateKey === 'B4_peak_day') {
    return CLEARED_PROOF_URLS.basket;
  }
  if (templateKey === 'B3_proposal_followup') {
    return CLEARED_PROOF_URLS.brandSpotlight;
  }
  return null;
}

function isPulseTemplate(templateKey: OutreachTemplateKey): boolean {
  return templateKey.startsWith('B_pulse_');
}

function buildMailtoHref(email: string | null, subject: string, body: string): string | null {
  if (!email?.trim()) return null;
  const params = new URLSearchParams({ subject, body });
  return `mailto:${encodeURIComponent(email.trim())}?${params.toString()}`;
}

export function buildNurtureDraft(
  context: OutreachContactContext,
  templateKey: OutreachTemplateKey,
  email: string | null,
  options?: {
    proofMetric?: ProofMetric | null;
    pulseIssue?: NurturePulseIssue | null;
    stateHint?: string | null;
  }
): NurtureEmailDraft {
  const pulseIssue = options?.pulseIssue ?? getNurturePulseIssueByTemplate(templateKey);
  const proofMetric = options?.proofMetric ?? null;

  if (pulseIssue || isPulseTemplate(templateKey)) {
    const issue = pulseIssue ?? suggestNurturePulseIssue({});
    const copyInput = {
      issue,
      context: {
        firstName: context.firstName,
        storeName: context.storeName,
      },
      proofMetric: proofMetric ?? undefined,
      stateHint: options?.stateHint,
    };
    const proof_url = proofUrlForNurturePulse(proofMetric);
    const subject = buildNurturePulseSubject(copyInput);
    const bodyResult = buildNurturePulseBody(copyInput);
    const proof_ready = proofMetric
      ? isProofWarmNurtureReady(proofMetric, { state: options?.stateHint }).ready
      : false;

    if (isNurturePulseBlocked(bodyResult)) {
      // Strict authority: no cleared proof means no copy and no mailto.
      return {
        template_key: issue.templateKey,
        subject,
        body: '',
        proof_url: null,
        mailto_href: null,
        pulse_issue: issue.issue,
        proof_metric_id: issue.proofMetricId,
        proof_ready: false,
        blocked_reasons: bodyResult.reasons,
      };
    }

    return {
      template_key: issue.templateKey,
      subject,
      body: bodyResult,
      proof_url,
      mailto_href: buildMailtoHref(email, subject, bodyResult),
      pulse_issue: issue.issue,
      proof_metric_id: issue.proofMetricId,
      proof_ready,
      blocked_reasons: null,
    };
  }

  const proof_url = proofUrlForTemplate(templateKey, proofMetric);
  const subject = buildEmailSubject(templateKey, context.storeName);
  const body = buildEmailBody(templateKey, {
    firstName: context.firstName,
    storeName: context.storeName,
    posVendor: context.posVendor,
    proofUrl: proof_url,
    incumbentVendor: null,
    hasCompetitorIncumbent: false,
  });
  return {
    template_key: templateKey,
    subject,
    body,
    proof_url,
    mailto_href: buildMailtoHref(email, subject, body),
  };
}

export function suggestNurtureTemplate(
  context: OutreachContactContext,
  events: ProspectNurtureSendEvent[],
  options?: { dealStage?: DealStage | null; nurtureNotes?: string | null; usePulseSequence?: boolean }
): OutreachTemplateKey {
  const usePulse = options?.usePulseSequence !== false;

  if (usePulse) {
    const lastSent = events.find((event) => event.event_type === 'sent' || event.event_type === 'manual_log');
    const lastTemplate = lastSent?.template_key as OutreachTemplateKey | undefined;
    if (lastTemplate && !lastTemplate.startsWith('B_pulse_')) {
      const sentAt = lastSent?.occurred_at ? new Date(lastSent.occurred_at).getTime() : 0;
      const daysSince = (Date.now() - sentAt) / (1000 * 60 * 60 * 24);
      if (daysSince >= 3 && daysSince < 8) return 'follow_up';
      if (daysSince >= 8 && daysSince < 21) return 'follow_up_2';
    }
    return suggestNurturePulseIssue({
      nurtureNotes: options?.nurtureNotes ?? context.notes,
      sendEvents: events,
    }).templateKey;
  }

  const lastSent = events.find((event) => event.event_type === 'sent' || event.event_type === 'manual_log');
  const lastTemplate = lastSent?.template_key as OutreachTemplateKey | undefined;

  if (lastTemplate?.startsWith('B') || lastTemplate === 'follow_up' || lastTemplate === 'follow_up_2') {
    const sentAt = lastSent?.occurred_at ? new Date(lastSent.occurred_at).getTime() : 0;
    const daysSince = (Date.now() - sentAt) / (1000 * 60 * 60 * 24);
    if (lastTemplate === 'follow_up' && daysSince >= 5) return 'follow_up_2';
    if (lastTemplate !== 'follow_up_2' && daysSince >= 3) return 'follow_up';
  }

  if (options?.dealStage === 'proposal_sent') return 'B3_proposal_followup';
  if (options?.dealStage === 'closed_lost') return 'B1_timing';

  const notes = context.notes.toLowerCase();
  if (notes.includes('proposal') || notes.includes('quoted')) return 'B3_proposal_followup';
  if (notes.includes('full rollout') || notes.includes('whole store') || notes.includes('not ready')) {
    return 'B2_one_station';
  }

  const month = new Date().getMonth();
  if (month === 3 || month === 10) return 'B4_peak_day';

  return 'B1_timing';
}

/**
 * @deprecated Display-only legacy hint. NOT eligibility authority — the
 * server-owned enrollment cohort (`network_pulse_list_cohort`) is the only
 * Track B eligibility source. This heuristic is one-directionally consistent
 * with the server: whenever it returns false for opt-out/closed-won reasons,
 * the server also excludes the contact. It must never gate or trigger a send.
 * Recorded as retired authority in the Network Pulse implementation plan.
 */
export function isTrackBDeal(deal: Deal): boolean {
  if (deal.nurture_opt_out || deal.stage === 'closed_won') return false;
  if (!deal.prospect_contact_email?.trim()) return false;
  if (deal.stage === 'closed_lost' || deal.stage === 'proposal_sent') return true;
  if (deal.stage === 'qualified') {
    const days = (Date.now() - new Date(deal.updated_at).getTime()) / (1000 * 60 * 60 * 24);
    return days >= 60;
  }
  return false;
}

/** @deprecated Display-only legacy hint — see isTrackBDeal. */
export function isTrackBBlueprintLead(lead: BlueprintLead): boolean {
  if (lead.nurture_opt_out) return false;
  if (!lead.contact_email?.trim()) return false;
  if (lead.status === 'converted' || lead.status === 'disqualified' || lead.status === 'spam') return false;
  const days = (Date.now() - new Date(lead.updated_at).getTime()) / (1000 * 60 * 60 * 24);
  return days >= 14;
}
